export enum EffectType {
    OPENAI_IMAGE_EDIT = 'openai-image-edit',
    OPENAI_IMAGE_EDIT_OPENROUTER = 'openai-image-edit-openrouter',
    GEMINI_IMAGE_EDIT = 'gemini-image-edit',
    GEMINI_IMAGE_EDIT_OPENROUTER = 'gemini-image-edit-openrouter',
    GEMINI_IMAGE_TRANSFER_STYLE_OPENROUTER = 'gemini-image-transfer-style-openrouter',
    CUTOUT_PRO_RMBG = 'cutout-pro-rmbg',
    CUTOUT_PRO_FACE_CUT_OUT = 'cutout-pro-face-cut-out',
    CUTOUT_PRO_CARTOON_SELFIE = 'cutout-pro-cartoon-selfie',
    REMOVE_BG = 'remove-bg',
    CUSTOMILY_REMOVE_BG = 'customily-remove-bg',
    CUSTOMILY_FACE_CUT_OUT = 'customily-face-cut-out',
    CUSTOMILY_FILTER_EFFECT = 'customily-filter-effect',
    PICWISH_UPSCALE = 'picwish-upscale-effect'
}

export const EditableEffectTypes = [
    EffectType.CUTOUT_PRO_CARTOON_SELFIE,
    EffectType.CUTOUT_PRO_FACE_CUT_OUT,
    EffectType.CUTOUT_PRO_RMBG,
    EffectType.GEMINI_IMAGE_EDIT,
    EffectType.GEMINI_IMAGE_EDIT_OPENROUTER,
    EffectType.GEMINI_IMAGE_TRANSFER_STYLE_OPENROUTER,
    EffectType.OPENAI_IMAGE_EDIT_OPENROUTER,
    EffectType.OPENAI_IMAGE_EDIT,
    EffectType.REMOVE_BG,
    EffectType.PICWISH_UPSCALE
];

export enum OpenAIQuality {
    HIGH = 'high',
    MEDIUM = 'medium',
    LOW = 'low',
    AUTO = 'auto'
}

export enum OpenAISize {
    SQUARE = '1024x1024',
    LANDSCAPE = '1536x1024',
    PORTRAIT = '1024x1536',
    AUTO = 'auto'
}

export enum OpenAIBackground {
    TRANSPARENT = 'transparent',
    OPAQUE = 'opaque',
    AUTO = 'auto'
}

export enum CutoutProCartoonType {
    'magic-mirror-avatar' = '0',
    'fairy-avatar' = '1',
    'dreamland-avatar' = '2',
    'manga-avatar' = '3',
    'k-pop-avatar' = '4',
    'k-pop' = '5',
    'pixel' = '6',
    'smile-avatar' = '7',
    'magic-mirror' = '8',
    'american-comic-avatar' = '9',
    'american-comic' = '10',
    'pixar-avatar' = '11_header',
    'pixar' = '11_full',
    'pinky-avatar' = '12_header',
    'pinky' = '12_full',
    '13-header' = '13_header',
    '13-full' = '13_full'
}

export enum CustomilyFilterType {
    aqua = 'aqua',
    pencil = 'pencil',
    blur = 'blur',
    cartoon = 'cartoon',
    edge = 'edge',
    grayscale = 'grayscale',
    hdr = 'hdr',
    invert = 'invert',
    sepia = 'sepia',
    sharpen = 'sharpen',
    sketch = 'sketch',
    white_removal = 'white_removal'
}

export enum RemoveBgQuality {
    HQ = 'hq',
    LQ = 'lq',
    AUTO = 'auto'
}

export enum CutoutProQuality {
    HQ = 'hq',
    LQ = 'lq',
    AUTO = 'auto'
}

export enum PicwishUpScaleFactor {
    AUTO = 'auto',
    X1 = '1',
    X2 = '2',
    X4 = '4'
}

export enum PicwishEnhancementType {
    CLEAN = 'clean',
    FACE = 'face'
}

export interface ImageEffectBase {
    id?: string;
    type: EffectType;
    presetId?: string;
}

export interface OpenAiImageEditType extends ImageEffectBase {
    type: EffectType.OPENAI_IMAGE_EDIT;
    prompt: string;
    quality: OpenAIQuality;
    background: OpenAIBackground;
    size: OpenAISize;
}

export interface OpenAiImageEditOpenRouterType extends ImageEffectBase {
    type: EffectType.OPENAI_IMAGE_EDIT_OPENROUTER;
    prompt: string;
    isMini?: boolean;
}

export interface GeminiImageEditType extends ImageEffectBase {
    type: EffectType.GEMINI_IMAGE_EDIT;
    prompt: string;
}

export interface GeminiImageEditOpenRouterType extends ImageEffectBase {
    type: EffectType.GEMINI_IMAGE_EDIT_OPENROUTER;
    prompt: string;
    isFree?: boolean;
}

export interface GeminiImageTransferStyleOpenRouterType extends ImageEffectBase {
    type: EffectType.GEMINI_IMAGE_TRANSFER_STYLE_OPENROUTER;
    prompt: string;
    referenceImageUrl: string;
    isFree?: boolean;
}

export interface CutoutProRmbgType extends ImageEffectBase {
    type: EffectType.CUTOUT_PRO_RMBG;
    bgColor: string;
    crop: boolean;
    cropMargin: string;
    quality: CutoutProQuality;
}

export interface CutoutProFaceCutOutType extends ImageEffectBase {
    type: EffectType.CUTOUT_PRO_FACE_CUT_OUT;
    bgColor: string;
    crop: boolean;
    quality: CutoutProQuality;
}

export interface CutoutProCartoonSelfieType extends ImageEffectBase {
    type: EffectType.CUTOUT_PRO_CARTOON_SELFIE;
    cartoonType: CutoutProCartoonType;
}

export interface RemoveBgType extends ImageEffectBase {
    type: EffectType.REMOVE_BG;
    bgColor: string;
    crop: boolean;
    cropMargin: string;
    quality: RemoveBgQuality;
}

export interface CustomilyRemoveBgType extends ImageEffectBase {
    type: EffectType.CUSTOMILY_REMOVE_BG;
}

export interface CustomilyFaceCutOutType extends ImageEffectBase {
    type: EffectType.CUSTOMILY_FACE_CUT_OUT;
}

export interface CustomilyFilterEffectType extends ImageEffectBase {
    type: EffectType.CUSTOMILY_FILTER_EFFECT;
    filterType: CustomilyFilterType;
}

export interface PicwishUpScaleEffectType extends ImageEffectBase {
    type: EffectType.PICWISH_UPSCALE;
    scaleFactor: PicwishUpScaleFactor;
    enhancementType: PicwishEnhancementType;
}

export type ImageEffectType =
    | OpenAiImageEditType
    | OpenAiImageEditOpenRouterType
    | GeminiImageEditType
    | GeminiImageEditOpenRouterType
    | GeminiImageTransferStyleOpenRouterType
    | CutoutProRmbgType
    | CutoutProFaceCutOutType
    | CutoutProCartoonSelfieType
    | RemoveBgType
    | CustomilyRemoveBgType
    | CustomilyFaceCutOutType
    | CustomilyFilterEffectType
    | PicwishUpScaleEffectType;
